import { useEffect, useState } from "react";
import { ChevronRight, Folder, FolderInput, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api";
import { PillButton } from "@/components/sources/shared";

// Shared folder browser for Plex and OneDrive. load(parentId|undefined) -> { folders: [{id,title,count?}], files }
const Browser = ({ rootLabel, load, onPick, cta, start }) => {
  const [trail, setTrail] = useState(start ? null : []);
  const [data, setData] = useState(null);
  const parent = trail?.[trail.length - 1]?.id;
  useEffect(() => {
    if (start) start().then(setTrail).catch(() => setTrail([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!trail) return;
    setData(null);
    load(parent).then(setData).catch((e) => { const error = formatApiError(e); toast.error(error); setData({ folders: [], files: 0, error }); });
  }, [parent, trail === null]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!trail) return <div className="flex h-72 items-center justify-center"><Loader2 className="animate-spin text-zinc-300" /></div>;
  const path = trail.map((t) => t.title).join("/");
  return (
    <div data-testid="folder-browser">
      <div className="mb-3 flex flex-wrap items-center gap-1 font-mono text-xs text-zinc-500" data-testid="folder-breadcrumbs">
        <button onClick={() => setTrail([])} className="hover:text-zinc-900" data-testid="folder-crumb-root">{rootLabel}</button>
        {trail.map((t, i) => (
          <span key={t.id} className="flex items-center gap-1"><ChevronRight size={11} />
            <button onClick={() => setTrail(trail.slice(0, i + 1))} className="hover:text-zinc-900" data-testid={`folder-crumb-${i}`}>{t.title}</button>
          </span>
        ))}
      </div>
      <div className="h-72 overflow-y-auto rounded-md border border-zinc-200">
        {!data ? <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-zinc-300" /></div>
          : data.error ? <p className="p-6 text-center text-sm text-red-600" data-testid="folder-error">Couldn't load folders: {data.error}</p>
          : !data.folders.length ? <p className="p-6 text-center text-sm text-zinc-400">No sub-folders here{data.files ? ` · ${data.files} videos` : ""}</p>
          : data.folders.map((f, i) => (
            <button key={f.id} onClick={() => setTrail([...trail, f])} data-testid={`folder-item-${i}`}
              className="flex w-full items-center gap-3 border-b border-zinc-100 px-4 py-2.5 text-left text-sm transition-colors last:border-0 hover:bg-zinc-50">
              <Folder size={15} className="text-amber-600" /><span className="flex-1 truncate">{f.title}</span>
              {f.count != null && <span className="font-mono text-[10px] text-zinc-400">{f.count}</span>}<ChevronRight size={14} className="text-zinc-300" />
            </button>
          ))}
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="truncate font-mono text-xs text-zinc-500" data-testid="folder-current">
          {path ? `/${path}` : "Pick a folder"}{data?.folders.length && data.files ? ` · ${data.files} videos here` : ""}
        </p>
        <PillButton onClick={() => onPick({ path, trail, id: trail[trail.length - 1]?.id, name: trail[trail.length - 1]?.title })} disabled={!path || !data || !!data.error} testid="folder-import-button">
          <FolderInput size={14} />{cta}
        </PillButton>
      </div>
    </div>
  );
};

export const FolderDialog = ({ open, onOpenChange, title, description, testid = "folder-dialog", cta = "Import only this folder", ...browser }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid={testid}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      {open && <Browser cta={cta} {...browser} onPick={(p) => { onOpenChange(false); browser.onPick(p); }} />}
    </DialogContent>
  </Dialog>
);
